import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useSWRConfig } from "swr";

import Button from "components/Button";
import { Board, PageTop, Title, TitleArea } from "components/LayoutParts";
import Loading from "components/Loading";
import color from "constants/color";
import font from "constants/font";
import usePageState from "hooks/usePageState";
import { useFindLatestAiSoftwareVersions } from "pages/AimoParking/AiSoftware/api";
import AimoParkingSelect from "pages/AimoParking/component/AimoParkingSelect";
import { useFindAllEdgeDevice } from "pages/AimoParking/EdgeDevice/api";
import BulkUpdateAiVersionModal from "pages/AimoParking/EdgeDevice/BulkUpdateAiVersionModal";
import DataTable from "pages/AimoParking/EdgeDevice/DataTable";
import { jumpUpToElement } from "utils/animationUtils";

const ButtonWrap = styled.div`
  margin-left: auto;
  display: flex;
  gap: 12px;
`;

const Margin = styled.div`
  margin-top: 16px;
`;

const FilterWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const FilterLabel = styled.span`
  font-weight: bold;
`;

const SelectionToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  border: 1px solid ${color.text.link};
  border-radius: 4px;
  background-color: ${color.text.linkLight};
`;

const SelectionCount = styled.span`
  font-size: ${font.size14};
  font-weight: bold;
`;

const ClearLink = styled.button`
  background: none;
  border: none;
  color: ${color.text.link};
  font-size: ${font.size14};
  cursor: pointer;
  padding: 0;
  &:hover {
    text-decoration: underline;
  }
`;

const EdgeDevice = () => {
  const [totalPages, setTotalPages] = useState<number>(1);
  const [selectedDeviceMap, setSelectedDeviceMap] = useState<Map<string, AimoParkingEdgeDeviceSummary>>(
    new Map()
  );
  const [isBulkModalVisible, setIsBulkModalVisible] = useState(false);

  const { currentPage, setCurrentPage, filter, setFilter } = usePageState<string, string>({
    pageSymbol: "aimoParkingEdgeDeviceListView",
    defaultFilter: "",
    filterParam: "ai_version",
  });
  const selectedAiSoftwareId = filter !== "" ? filter : null;
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const { data: latestAiSoftwares } = useFindLatestAiSoftwareVersions();
  const { data: edgeDevices, pageInfo } = useFindAllEdgeDevice({
    pageIndex: currentPage,
    aiSoftwareId: selectedAiSoftwareId ?? undefined,
  });

  const latestList = Array.isArray(latestAiSoftwares)
    ? latestAiSoftwares
    : latestAiSoftwares != null
      ? [latestAiSoftwares as AimoParkingAiSoftwareResponse]
      : [];
  const aiVersionChoices = [
    { label: "すべて", value: "" },
    ...latestList.map((item) => ({ label: item.version, value: item.id })),
  ];
  const selectedChoice =
    selectedAiSoftwareId === null || selectedAiSoftwareId === ""
      ? { label: "すべて", value: "" }
      : aiVersionChoices.find((c) => c.value === selectedAiSoftwareId) ?? null;

  const selectedDevices = useMemo(() => Array.from(selectedDeviceMap.values()), [selectedDeviceMap]);
  const selectedIds = useMemo(() => new Set(selectedDeviceMap.keys()), [selectedDeviceMap]);

  const onClickEdgeDeviceAdd = () => {
    navigate("/aimo_parking/edge_device/create");
  };

  const onAiVersionChange = (choice: { label: string; value: string }) => {
    setFilter(choice.value === "" ? "" : choice.value);
  };

  const handleToggle = useCallback((device: AimoParkingEdgeDeviceSummary) => {
    setSelectedDeviceMap((prev) => {
      const next = new Map(prev);
      if (next.has(device.id)) next.delete(device.id);
      else next.set(device.id, device);
      return next;
    });
  }, []);

  const handleToggleAllOnPage = useCallback((devices: AimoParkingEdgeDeviceSummary[]) => {
    setSelectedDeviceMap((prev) => {
      const allSelected = devices.length > 0 && devices.every((d) => prev.has(d.id));
      const next = new Map(prev);
      if (allSelected) {
        for (const d of devices) next.delete(d.id);
      } else {
        for (const d of devices) next.set(d.id, d);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedDeviceMap(new Map());
  }, []);

  const refreshList = useCallback(() => {
    void mutate(
      (key: unknown) =>
        Array.isArray(key) && typeof key[0] === "string" && key[0].startsWith("/edge-device")
    );
  }, [mutate]);

  const handleAllSucceeded = useCallback(
    (updatedIds: string[]) => {
      setSelectedDeviceMap((prev) => {
        const next = new Map(prev);
        for (const id of updatedIds) next.delete(id);
        return next;
      });
      refreshList();
    },
    [refreshList]
  );

  useEffect(() => {
    setTotalPages(pageInfo?.totalPages ?? 1);
    return () => {
      jumpUpToElement("root");
    };
  }, [pageInfo?.totalPages]);

  return (
    <Board>
      <PageTop />
      <TitleArea>
        <Title>エッジデバイス</Title>
        <ButtonWrap>
          <Button label="エッジデバイスを登録" onClick={onClickEdgeDeviceAdd} type="primary" width="191px" />
        </ButtonWrap>
      </TitleArea>
      <Margin />
      <FilterWrap>
        <FilterLabel>AIバージョン</FilterLabel>
        <AimoParkingSelect
          choices={aiVersionChoices}
          value={selectedChoice}
          onChange={onAiVersionChange}
          width="200px"
          placeholder="すべて"
        />
      </FilterWrap>
      {selectedDevices.length > 0 && (
        <SelectionToolbar>
          <SelectionCount>{selectedDevices.length} 台を選択中</SelectionCount>
          <ClearLink onClick={clearSelection}>選択を解除</ClearLink>
          <div style={{ marginLeft: "auto" }}>
            <Button
              label="AIバージョンを一括更新"
              type="primary"
              onClick={() => setIsBulkModalVisible(true)}
              width="220px"
            />
          </div>
        </SelectionToolbar>
      )}
      {edgeDevices ? (
        <DataTable
          edgeDeviceList={edgeDevices}
          totalPages={totalPages}
          pageIndex={currentPage}
          setPageIndex={setCurrentPage}
          selection={{
            selectedIds,
            onToggle: handleToggle,
            onToggleAllOnPage: handleToggleAllOnPage,
          }}
        />
      ) : (
        <Loading />
      )}
      <BulkUpdateAiVersionModal
        isVisible={isBulkModalVisible}
        selectedDevices={selectedDevices}
        onClose={() => setIsBulkModalVisible(false)}
        onAllSucceeded={handleAllSucceeded}
      />
    </Board>
  );
};

export default EdgeDevice;
